import { useEffect, useRef, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { api } from "../api";
import { ComingSoon } from "../features/launch/ComingSoon";
import { authStore, consumeLoginNotice, currentOrg, mfaSetupPending, useAuth, type AuthState } from "./store";
import { Skeleton, ErrorState } from "../components/States";
import { MfaGate } from "./MfaGate";
import { OrgSetupGate, organizationSetupComplete } from "./OrgSetupGate";

/**
 * True when the principal owes MFA enrollment. The `setup_mfa` step from the
 * login result comes first: it is the only signal available on the paint right
 * after signing in, before /v1/me has been fetched. The mfa state from /v1/me
 * covers reloads, where there is no login result to carry a hint.
 */
export function needsMfaSetup(s: AuthState): boolean {
  if (s.status !== "authed") return false;
  return s.next === "setup_mfa" || mfaSetupPending(s.me?.mfa);
}

/**
 * Route guard for cookie-authenticated pages. While the principal is unknown it
 * shows a skeleton; anonymous users are redirected to /login with `next`; users
 * who still owe email verification are redirected to that step.
 *
 * Users who owe MFA enrollment get a blocking gate rendered in place of the app
 * shell, so nothing else in the product is visible until a factor exists.
 *
 * Redirects run in an effect (not via <Navigate>) so a render never triggers a
 * navigation synchronously, which keeps the guard loop-free under StrictMode.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const pathname = loc.pathname;
  const searchStr = loc.searchStr ?? "";

  useEffect(() => {
    if (auth.status === "unknown" && !auth.error) void authStore.load();
  }, [auth.status, auth.error]);

  const redirect: { to: string; search?: Record<string, string>; replace: true } | null =
    auth.status === "anon"
      ? auth.setupRequired
        ? { to: "/setup", replace: true }
        : { to: "/login", search: { next: pathname + searchStr }, replace: true }
      : auth.next === "verify_email" && !pathname.startsWith("/verify-email")
        ? { to: "/verify-email", replace: true }
        : null;

  // Navigate exactly once per redirect decision. During a router transition the
  // old match (this guard) can still render against the new location, so a
  // naive effect would re-navigate with next="/login" and loop.
  const redirected = useRef<string | null>(null);
  useEffect(() => {
    if (!redirect) {
      redirected.current = null;
      return;
    }
    if (redirected.current === redirect.to) return;
    if (redirect.to === "/login" && pathname.startsWith("/login")) return;
    redirected.current = redirect.to;
    const search = { ...(redirect.search ?? {}) };
    const notice = consumeLoginNotice();
    if (notice && redirect.to === "/login") search.msg = notice;
    void navigate({ to: redirect.to as "/login", search: search as never, replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirect?.to]);

  if (auth.status === "unknown") {
    if (auth.error) return <ErrorState error={{ message: auth.error, code: "network" }} retry={() => void authStore.load()} />;
    return <Skeleton lines={6} />;
  }
  if (redirect) return <Skeleton lines={3} />;
  if (needsMfaSetup(auth)) return <MfaGate />;
  const org = currentOrg(auth);
  if (org && !organizationSetupComplete(org)) return <OrgSetupGate key={org.id} org={org} />;
  if (!auth.me?.user.staff) {
    return <ProductGate>{children}</ProductGate>;
  }
  return <>{children}</>;
}

/** While the product is closed, only platform staff get past this page. */
function ProductGate({ children }: { children: ReactNode }) {
  const availability = useQuery({
    queryKey: ["launch", "availability"],
    queryFn: () => api.launch.availability(),
    staleTime: 30_000,
  });
  if (availability.isPending) return <Skeleton lines={6} />;
  if (availability.data && availability.data.open === false) return <ComingSoon signedIn />;
  return <>{children}</>;
}
